import { Link } from "react-router-dom";
import { useState } from "react";
import logo1 from "../assets/thrombiocolourlogo1.webp";
import aboutIcon from "../assets/aboutIcon.webp";
import aboutIconMobile from "../assets/aboutIconMobile.webp";
import publicationMediaIcon from "../assets/publicationsIcon.webp";
import publicationMediaIconMobile from "../assets/publicationsIconMobile.webp";
import leadershipIcon from "../assets/leadershipIcon.webp";
import leadershipIconMobile from "../assets/leadershipIconMobile.webp";
import olcdots from "../assets/olcdots.webp";
import technologyIcon from "../assets/circle31.webp";
import mobileCurveFooter from "../assets/curveimgnewnewnewmobile62.webp";
import HomeOverlay from "../components/HomeOverlay";
import LazyImage from "../components/LazyImage";
import SEO from "../components/SEO";

export default function Home() {
  const [showOverlay, setShowOverlay] = useState(true);

  const handleCloseOverlay = () => {
    setShowOverlay(false);
  };
  
  // Schema data for the Home page
  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Thrombio",
    "url": "https://www.thrombio.com.au/",
    "logo": "https://www.thrombio.com.au/thromBioInvert.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "info@thrombio.com.au"
    },
    "sameAs": [
      "https://www.linkedin.com/company/thrombioholdings"
    ],
    "description": "Thrombio is a drug discovery company dedicated to advancing the global standard of care for the treatment of stroke using novel anti-thrombotic drugs with unprecedented safety and efficacy."
  };

  return (
    <>
      <SEO 
        title="Revolutionizing Stroke Care"
        description="Building on 30 years of world-leading research in blood clotting and stroke, TBO-309 shows promising Phase II data, potentially revolutionizing stroke care worldwide."
        keywords={['Stroke Treatment', 'ThromBio', 'Drug Development', 'TBO-309', 'Biotechnology']}
        ogImage="/Thrombio.gif"
        schema={homeSchema}
      />

      <div className="bg-primary homeBg -mb-80 pb-80">
        <div className="relative md:pageContainer">
          <div className="flex max-xl:flex-col max-xl:gap-10 px-6 md:px-12 xl:items-center justify-center pb-32 xl:pb-[40rem]">
            <div className="flex flex-col gap-8">
              <img 
                id="heroLogo" 
                src={logo1} 
                alt="Thrombio logo - Innovators in Stroke Therapies" 
                width={439}
                className="h-auto max-w-full"
              />
              <div className="text-5xl tracking-wider">
                <h1 className="sr-only">ThromBio: Leading Innovations in Stroke Treatment</h1>
                Innovators in Stroke Therapy
              </div>
            </div>
            <div className="flex justify-end">
              <img src="/Thrombio.gif" alt="Animated visualization of brain and stroke related medical imagery" width={656} className="max-w-full h-auto" />
            </div>
          </div>
          
          {showOverlay && <HomeOverlay onClose={handleCloseOverlay} />}
          
          <div className="pageContainer flex flex-col gap-24 pt-0 pb-[19rem] md:pb-[16rem]">
            <div className="grid md:grid-cols-2 justify-evenly items-center gap-12 xl:gap-28 md:max-xl:pr-[2.7rem] xl:pl-[4.4rem]">
              <div className="flex flex-col gap-[2.8rem] max-w-prose">
                <Link to="/about" className="w-fit homepageHeading">
                  About
                  <span className="sr-only">ThromBio: Pioneers in Stroke Therapy</span>
                </Link>
                <p className="homepageText">
                  ThromBio is a drug discovery company whose mission is to advance the global standard of care for the
                  treatment of stroke using novel anti-thrombotic drugs with unprecedented safety and efficacy
                </p>
              </div>
              <div className="rounded-full aspect-square flex justify-center items-center md:p-4 md:bg-gradient-to-br from-black to-secondary-bg from-60%">
                <LazyImage
                  src={aboutIcon}
                  alt="Two SEM imaging of resting platelets for stroke research"
                  className="rounded-full object-contain bg-black aspect-square max-md:hidden"
                  width={400}
                  height={400}
                  eager={true}
                />
                <LazyImage 
                  src={aboutIconMobile} 
                  alt="Two SEM imaging of resting platelets for stroke research" 
                  className="h-96 md:object-contain bg-black md:hidden"
                  width={300}
                  height={384}
                  eager={true}
                />
              </div>
            </div>
          </div>
          <div className="pageContainer flex flex-col gap-24 pt-0 pb-[19rem] md:pb-[16rem]">
            <div className="grid md:grid-cols-2 justify-evenly items-center xl:gap-28 md:max-xl:pl-[2.7rem] xl:pr-[4.4rem]">
              <div className="flex flex-col gap-[2.8rem] max-w-prose">
                <Link to="/leadership/board-of-directors" className="homepageHeading w-fit">
                  Leadership
                  <span className="sr-only">Leading Experts in Stroke Therapy</span>
                </Link>
                <p className="homepageText">
                  ThromBio programme is driven by international leaders in drug discovery & development, experimental
                  biomedicine, global clinical trials capability and commercialisation
                </p>
              </div>
              <div className="rounded-full aspect-square flex justify-center items-center md:p-4 md:bg-gradient-to-b from-black to-secondary-bg from-60%">
                <LazyImage
                  src={leadershipIcon}
                  alt="SEM imaging of an activated platelet for stroke research"
                  className="rounded-full object-contain bg-black aspect-square max-md:hidden"
                  width={400}
                  height={400}
                  eager={true}
                />
                <LazyImage 
                  src={leadershipIconMobile} 
                  alt="SEM imaging of an activated platelet for stroke research" 
                  className="h-96 md:object-contain bg-black md:hidden"
                  width={300}
                  height={384}
                  eager={true}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center px-12 md:px-8 bg-no-repeat bg-left homeTechnologyBg">
            <LazyImage 
              src={olcdots} 
              alt="Decorative dot pattern" 
              width={37} 
              height={23} 
              className="md:hidden mb-[11.2rem]"
              eager={false}
            />

            <div className="grid xl:grid-cols-2 w-full">
              <div className="flex flex-col gap-16 xl:gap-10 md:mb-24">
                <div className="font-syne md:text-primary-blue font-bold text-[3.4rem] md:text-[4rem] xl:text-[4.2rem] leading-[3.6rem] tracking-[5%] md:tracking-[.2rem] text-center max-xl:mb-10">
                  Technology
                  <span className="sr-only">Cutting-Edge Anti-Thrombotic Solutions</span>
                </div>
                <LazyImage 
                  src={technologyIcon} 
                  alt="SEM imaging of a group activated platelets for stroke research" 
                  className="max-xl:hidden"
                  width={500}
                  height={500}
                  eager={false}
                />
              </div>
              <div className="homeTechnologyContentBg flex flex-col md:items-end max-md:gap-[21rem] gap-16 w-full">
                <div className="flex flex-col gap-8 md:items-end">
                  <div className="flex items-start gap-8 xl:gap-16">
                    <LazyImage 
                      src={olcdots} 
                      alt="Decorative dot pattern" 
                      width={37} 
                      height={23} 
                      className="max-xl:hidden shrink mt-3"
                      eager={false}
                    />
                    <Link to="/technology/candidate" className="homepageHeading w-fit">
                      <div className="md:text-right font-syne text-[#C3EEFD] font-bold text-[2.6rem] md:text-[3.3rem] leading-[3.2rem] tracking-[1%] grow text-nowrap">
                        Our Lead Candidate: <br />
                        <span>TBO-309</span>
                        <span className="sr-only">Breakthrough Anti-Thrombotic Drugs for Stroke</span>
                      </div>
                    </Link>
                  </div>
                  <p className="text-[1.9rem] md:text-[2rem] md:leading-[3rem] md:max-xl:max-w-[250px] md:text-right">
                    TBO-309 represents an Australian drug development success story, that has resulted in a novel
                    antiplatelet compound with truly remarkable properties
                  </p>
                </div>
                <div className="flex flex-col gap-8 xl:pr-32">
                  <div className="flex items-start justify-end gap-8 xl:gap-16">
                    <LazyImage 
                      src={olcdots} 
                      alt="Decorative dot pattern" 
                      width={37} 
                      height={23} 
                      className="max-xl:hidden shrink mt-3"
                      eager={false}
                    />
                    <Link to="/technology/leadIndication" className="homepageHeading w-fit">
                      <div className="font-syne text-[#C3EEFD] font-bold text-[2.6rem] md:text-[3.3rem] leading-[3.2rem] tracking-[1%] text-right">
                        Lead Indications
                        <span className="sr-only">TBO-309 for Advanced Stroke Treatment</span>
                      </div>
                    </Link>
                  </div>

                  <p className="text-[1.9rem] md:text-[2rem] md:leading-[3rem] tracking-[2%] md:max-w-[400px] md:text-right ">
                    Acute Ischaemic stroke remains the second largest cause of death and disability globally, with 85%
                    of all strokes caused by blood clots that block blood flow to the brain <br />
                    TBO-309 is currently being trialled in patients in Phase II clinical trials, as an adjunct to
                    thrombolysis (STARS)
                  </p>
                </div>
              </div>
            </div>
            <LazyImage 
              src={olcdots} 
              alt="Decorative dot pattern" 
              width={37} 
              height={23} 
              className="md:hidden mt-[11.2rem]"
              eager={false}
            />
          </div>
          <div className="max-md:mt-[190px] xl:w-[862px] xl:h-[829px] flex max-md:flex-col justify-center items-center relative mx-auto">
            <div className="md:hidden">
              <a href="/publications" className="text-[#C3EEFD] text-[2.9rem] font-bold leading-[3.6rem] font-syne text-center w-fit">
                Publications
                <span className="sr-only">ThromBio Publications: Leading Research in Stroke Therapy</span>
              </a>
            </div>

          <div className="rounded-full aspect-square flex justify-center items-center md:p-2 md:bg-gradient-to-bl from-black to-secondary-bg from-60%">
            <LazyImage 
              src={publicationMediaIconMobile} 
              alt="Initial adhesion of a platelet captured by scanning electron microscopy (SEM) for stroke research" 
              className="rounded-full max-h-[500px] md:hidden"
              width={400}
              height={400}
              eager={false}
            />
            <LazyImage 
              src={publicationMediaIcon} 
              alt="Initial adhesion of a platelet captured by scanning electron microscopy (SEM) for stroke research" 
              className="rounded-full max-h-[500px] max-md:hidden"
              width={500}
              height={500}
              eager={false}
            />
          </div>
          <div className="max-md:hidden absolute md:h-[383px] w-full left-0 top-1/2 -translate-y-1/2">
            <div className="inset-0 absolute flex flex-col items-stretch gap-3">
              <a href="/publications" className="text-primary-blue text-[4.8rem] font-bold leading-[3.6rem] font-syne w-fit">
                Publications
                <span className="sr-only">ThromBio Publications: Leading Research in Stroke Therapy</span>
              </a>
              <div className="block bg-[#00354C] min-h-[4px] w-[328px]"></div>
            </div>
            <div className="right-0 bottom-0 absolute flex flex-col items-stretch gap-3">
              <a href="/media" className="text-primary-blue text-[4.8rem] font-bold leading-[3.6rem] font-syne">
                Media
                <span className="sr-only">ThromBio Media: Latest News and Press Releases on Stroke Therapy</span>
              </a>
              <div className="block bg-[#00354C] min-h-[4px] w-[170px]"></div>
            </div>
          </div>

            <div className="md:hidden">
              <a href="/media" className="text-[#C3EEFD] text-[2.9rem] font-bold leading-[3.6rem] font-syne text-center">
                Media
                <span className="sr-only">ThromBio Media: Latest News and Press Releases on Stroke Therapy</span>
              </a>
            </div>
          </div>
          <div className="w-full">
            <LazyImage 
              src={mobileCurveFooter} 
              alt="Curve-pattern background graphic" 
              className="md:hidden -mt-40 object-cover max-w-none w-full"
              width={375}
              height={200}
              eager={false}
            />
          </div>
        </div>
      </div>
    </>
  );
}